import {
  AppError,
  ErrorCode,
  type EventPayload,
  EventType,
  FatalError,
  isFactorLockError,
  RetryableError,
} from '@imprivata-cloud/authn';
import { createSpanName, debug, SpanComponent } from '@imprivata-cloud/common';
import { useCallback } from 'react';

const ERROR_SCREEN_CODES = new Set<ErrorCode>([
  ErrorCode.UNSUPPORTED_INLINE_ENROLLMENT,
  ErrorCode.ACCOUNT_DISABLED,
  ErrorCode.ACCOUNT_LOCKED,
  ErrorCode.UNSUPPORTED_OPERATION,
]);

function toAppError(
  data: AppError | string,
  fallbackCode: ErrorCode = ErrorCode.UNKNOWN,
): AppError {
  return typeof data === 'string'
    ? new AppError({ message: data, code: fallbackCode })
    : data;
}

// Raw backend wire code returned by authn-session/start when the username
// cannot be resolved. It has no ErrorCode enum member (the enum's NOT_FOUND is
// the lowercase EPCS "not-found"), and the module's 5xx interceptor escalates
// it to a FatalError.
const BACKEND_USER_NOT_FOUND_CODE = 'NOT_FOUND';

// Username-resolution failures the user recovers from by re-entering their
// username. authn-session/start answers these with HTTP 500, so the authn
// module's 5xx interceptor escalates them to FatalError — which would otherwise
// redirect to the relying party instead of showing a correctable banner.
const RECOVERABLE_USERNAME_ERROR_CODES = new Set<string>([
  ErrorCode.AMBIGUOUS_USERNAME,
  ErrorCode.NOT_FOUND,
  BACKEND_USER_NOT_FOUND_CODE,
]);

function isRecoverableUsernameError(error: AppError): boolean {
  return (
    error.code !== undefined && RECOVERABLE_USERNAME_ERROR_CODES.has(error.code)
  );
}

// The attempted username the authn module carries on the error's metadata bag
// (`details.data.username`, the same bag `getUserUpnFromError` reads `user-upn`
// from). Echoed back to the relying party as `login_hint` on the failure redirect
// so it can pre-fill its login form on retry (undefined in 1:N flows, where no
// username was entered).
export function loginHintOf(error: AppError): string | undefined {
  const username = error.details?.data?.username;
  return typeof username === 'string' ? username : undefined;
}

// Rebuild a (possibly Fatal) error as a plain, banner-eligible AppError so the
// shared banner path surfaces it — useBanner suppresses FatalError instances.
function toRecoverableError(error: AppError): AppError {
  return new AppError(
    { code: error.code ?? ErrorCode.UNKNOWN, message: error.message },
    error.params,
  );
}

interface UseEventHandlerDeps {
  // Control flow: terminal/redirect transitions of the top-level flow.
  onSuccess: (opts: { authorizationCode: string }) => void | Promise<void>;
  onFailure: (options: {
    redirectDelayMs?: number;
    errorCode?: string;
    errorDescription?: string;
    loginHint?: string;
  }) => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  setUsername: (username: string | undefined) => void;
  // Orthogonal side-channels: the transient notification banner and error screen.
  displayBanner: (err: AppError) => void;
  clearBanner: () => void;
  setScreenError: (error: AppError | undefined) => void;
  // View configuration: a single transition of the unified presentation state.
  dispatchView: (payload: EventPayload) => void;
}

export function useEventHandler({
  onSuccess,
  onFailure,
  onCancel,
  setUsername,
  displayBanner,
  clearBanner,
  setScreenError,
  dispatchView,
}: UseEventHandlerDeps) {
  const handleEvent = useCallback(
    async (payload: EventPayload) => {
      const { event, data } = payload;
      debug('Received event', { event, data });
      createSpanName(SpanComponent.UI, 'event', `on-event--${event}`);

      if (event === EventType.AUTHENTICATED) {
        debug('Authentication successful');
        await onSuccess({
          authorizationCode: data?.oidcAuthorizationCode ?? '',
        });
        return;
      }

      if (event === EventType.CANCEL) {
        debug('Authentication canceled by user');
        await onCancel();
        return;
      }

      if (event === EventType.ERROR) {
        // A username-resolution failure from authn-session/start (e.g. the raw
        // "NOT_FOUND" at HTTP 500) is escalated to FatalError by the module's
        // 5xx interceptor. Demote it: clear the username to return to the
        // UserNameForm and show a banner instead of redirecting to the RP.
        if (data instanceof FatalError && isRecoverableUsernameError(data)) {
          setUsername(undefined);
          displayBanner(toRecoverableError(data));
          return;
        }

        if (data instanceof FatalError) {
          return await onFailure({
            errorCode: data.code,
            errorDescription: data.message,
            loginHint: loginHintOf(data),
          });
        }

        if (data instanceof AppError) {
          if (
            data.code === ErrorCode.AMBIGUOUS_USERNAME ||
            data.code === ErrorCode.USER_NOT_FOUND
          ) {
            setUsername(undefined);
          }

          if (ERROR_SCREEN_CODES.has(data.code as ErrorCode)) {
            clearBanner();
            setScreenError(data);
            return;
          }

          if (isFactorLockError(data)) {
            clearBanner();
            return;
          }

          if (data.code === ErrorCode.FACE_TIMEOUT) {
            await onFailure({
              errorCode: data.code,
              errorDescription: data.message,
              loginHint: loginHintOf(data),
            });
            clearBanner();
            return;
          }

          if (
            data.code ===
              ErrorCode.GENERIC_FACE_AUTHENTICATION_ERROR_WITHOUT_BANNER ||
            data.code === ErrorCode.LIVENESS_FAILED ||
            data.code === ErrorCode.FACTOR_CAPTURE_FAILED
          ) {
            clearBanner();
            return;
          }

          displayBanner(data);
          return;
        }
      }

      switch (event) {
        case EventType.OFFLINE:
          displayBanner(new RetryableError({ code: ErrorCode.OFFLINE }));
          break;

        case EventType.ONLINE:
        case EventType.CLEAR_BANNER:
        case EventType.RESTART_STATE:
        // Reset the in-place flow (enroll/account-recovery) by clearing the
        // transient banner and screen error.
        case EventType.INTERNAL_RESTART:
          clearBanner();
          setScreenError(undefined);
          break;

        case EventType.IMPR_ID_ERROR:
        case EventType.INTERNAL_ERROR:
          if (data) {
            if (data instanceof FatalError) {
              void onFailure({
                errorCode: data.code,
                errorDescription: data.message,
                loginHint: loginHintOf(data),
              });
            }
            displayBanner(toAppError(data));
          }
          break;

        case EventType.CAMERA_ERROR:
        case EventType.PERMISSION_REVOKED:
          if (data?.code === ErrorCode.DEVICE_INITIALIZATION_TIMEOUT) {
            return await onFailure({
              errorCode: data.code,
              errorDescription: data.message,
              loginHint: loginHintOf(data),
            });
          }
          if (data) {
            displayBanner(data);
          }
          break;

        case EventType.DEVICE_DISCONNECTED:
          if (data) {
            displayBanner(toAppError(data, ErrorCode.DEVICE_DISCONNECTED));
          }
          break;

        case EventType.PERMISSION_GRANTED:
        case EventType.DEVICE_RECONNECTED:
          clearBanner();
          break;

        // View configuration: the header, hex glyph and layout flags are pure
        // projections of one presentation state, so these only transition it.
        // Entering the face-capture screen (VIEW_CHANGED -> FACE_ID) retires the
        // start-enroll hero for the rest of the enroll flow (see reduceView).
        case EventType.START_ENROLLING:
        case EventType.START_RECONSENT:
        case EventType.START_AUTHENTICATING:
        case EventType.FINISH_AUTHENTICATING:
        case EventType.VIEW_CHANGED:
          dispatchView(payload);
          break;

        default:
          break;
      }
    },
    [
      onSuccess,
      onFailure,
      onCancel,
      setUsername,
      displayBanner,
      clearBanner,
      setScreenError,
      dispatchView,
    ],
  );

  return handleEvent;
}
